import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ro from 'date-fns/locale/ro';
import { format } from 'date-fns';
import { useAuth } from '@contexts/AuthContext';
import type { Appointment } from '@app-types/reception';

// Componente
import { StatsBar } from './components/StatsBar';
import { FiltersBar } from './components/FiltersBar';
import { QuickActions } from './components/QuickActions';
import { AppointmentTable } from './components/AppointmentTable';
import { DelayAlerts } from './components/DelayAlerts';
import { PaginationBar } from './components/PaginationBar';
import { CheckinModal } from './components/CheckinModal';
import { StartConsultationModal } from './components/StartConsultationModal';
import { FinishVisitModal } from './components/FinishVisitModal';
import { CancelAppointmentModal } from './components/CancelAppointmentModal';

// Hooks
import { useReceptionData } from './hooks/useReceptionData';
import { useReceptionSocket } from './hooks/useReceptionSocket';
import clinicsService from '@services/clinicsService';

const ReceptionDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const clinicId = user?.clinicId || '';

  // Data curenta
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  // Hook pentru date
  const {
    appointments,
    stats,
    delayAlerts,
    loading,
    error,
    filters,
    pagination,
    refresh,
    checkin,
    changeStatus,
    cancelAppointment,
    updateFilters,
    changePage,
    changePageSize
  } = useReceptionData(dateStr, clinicId);

  const [doctors, setDoctors] = useState<
    Array<{ id: string; firstName: string; lastName: string }>
  >([]);

  useEffect(() => {
    if (!clinicId) {
      setDoctors([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await clinicsService.getClinicVeterinarians(clinicId);
        if (cancelled) return;
        const mapped = (Array.isArray(list) ? list : []).map(
          (v: { id: string; firstName?: string; lastName?: string }) => ({
            id: v.id,
            firstName: v.firstName || '',
            lastName: v.lastName || ''
          })
        );
        setDoctors(mapped);
      } catch (e) {
        console.warn('Recepție: nu s-au putut încărca veterinarii pentru filtre', e);
        if (!cancelled) setDoctors([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clinicId]);

  // Hook pentru WebSocket
  useReceptionSocket(clinicId, refresh);

  // State pentru modale
  const [checkinModal, setCheckinModal] = useState<{
    open: boolean;
    appointment: Appointment | null;
  }>({ open: false, appointment: null });

  const [startModal, setStartModal] = useState<{
    open: boolean;
    appointment: Appointment | null;
  }>({ open: false, appointment: null });

  const [finishModal, setFinishModal] = useState<{
    open: boolean;
    appointment: Appointment | null;
  }>({ open: false, appointment: null });

  const [cancelModal, setCancelModal] = useState<{
    open: boolean;
    appointment: Appointment | null;
  }>({ open: false, appointment: null });

  // Handlers pentru actiuni pe randuri
  const handleCheckinClick = (appointmentId: string) => {
    const appointment = appointments.find((a) => a.id === appointmentId);
    if (appointment) {
      setCheckinModal({ open: true, appointment });
    }
  };

  const handleStartClick = (appointmentId: string) => {
    const appointment = appointments.find((a) => a.id === appointmentId);
    if (appointment) {
      setStartModal({ open: true, appointment });
    }
  };

  const handleFinishClick = (appointmentId: string) => {
    const appointment = appointments.find((a) => a.id === appointmentId);
    if (appointment) {
      setFinishModal({ open: true, appointment });
    }
  };

  const handleCancelClick = (appointmentId: string) => {
    const appointment = appointments.find((a) => a.id === appointmentId);
    if (appointment) {
      setCancelModal({ open: true, appointment });
    }
  };

  // Handlers pentru confirmare actiuni
  const handleCheckinConfirm = async (appointmentId: string) => {
    await checkin(appointmentId);
  };

  const handleStartConfirm = async (appointmentId: string) => {
    await changeStatus(appointmentId, 'in_consultation');
  };

  const handleFinishConfirm = async (appointmentId: string) => {
    await changeStatus(appointmentId, 'finished');
  };

  const handleCancelConfirm = async (appointmentId: string, reason?: string) => {
    await cancelAppointment(appointmentId, reason);
  };

  const handleAddAppointment = () => navigate('/receptie/appointments');
  const handleQuickSearch = () => navigate('/receptie/appointments');

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Box mb={3}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            📋 Dashboard Receptie
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestioneaza programarile si fluxul de pacienti
          </Typography>
        </Box>

        {/* Selector data */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <DatePicker
              label="Selecteaza data"
              value={selectedDate}
              onChange={(newDate) => {
                if (newDate) {
                  setSelectedDate(newDate);
                }
              }}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 200 }
                }
              }}
            />
            <Button variant="outlined" onClick={() => setSelectedDate(new Date())}>
              Astazi
            </Button>
          </Box>
        </Paper>

        {/* Statistici */}
        <Box mb={3}>
          <StatsBar stats={stats} loading={loading} />
        </Box>

        {/* Alerte intarzieri */}
        {delayAlerts.length > 0 && (
          <Box mb={3}>
            <DelayAlerts alerts={delayAlerts} />
          </Box>
        )}

        {/* Actiuni rapide */}
        <Box mb={3}>
          <QuickActions
            onAddAppointment={handleAddAppointment}
            onQuickSearch={handleQuickSearch}
            onRefresh={refresh}
          />
        </Box>

        {/* Filtre */}
        <Box mb={3}>
          <FiltersBar
            filters={filters}
            onFiltersChange={updateFilters}
            doctors={doctors}
          />
        </Box>

        {/* Eroare */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Tabel programari */}
        <Box mb={3}>
          <AppointmentTable
            appointments={appointments}
            loading={loading}
            error={error}
            onCheckin={handleCheckinClick}
            onStartConsultation={handleStartClick}
            onFinish={handleFinishClick}
            onCancel={handleCancelClick}
          />
        </Box>

        {/* Paginare */}
        {appointments.length > 0 && (
          <PaginationBar
            page={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onPageChange={changePage}
            onPageSizeChange={changePageSize}
          />
        )}

        {/* Modale */}
        <CheckinModal
          open={checkinModal.open}
          appointment={checkinModal.appointment}
          onClose={() => setCheckinModal({ open: false, appointment: null })}
          onConfirm={handleCheckinConfirm}
        />

        <StartConsultationModal
          open={startModal.open}
          appointment={startModal.appointment}
          onClose={() => setStartModal({ open: false, appointment: null })}
          onConfirm={handleStartConfirm}
        />

        <FinishVisitModal
          open={finishModal.open}
          appointment={finishModal.appointment}
          onClose={() => setFinishModal({ open: false, appointment: null })}
          onConfirm={handleFinishConfirm}
        />

        <CancelAppointmentModal
          open={cancelModal.open}
          appointment={cancelModal.appointment}
          onClose={() => setCancelModal({ open: false, appointment: null })}
          onConfirm={handleCancelConfirm}
        />
      </Container>
    </LocalizationProvider>
  );
};

export default ReceptionDashboard;

